import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, TruckIcon, ShieldCheckIcon, AlertTriangleIcon, TrendingUpIcon, CheckCircleIcon, ClockIcon, XCircleIcon, AlertCircleIcon, FileTextIcon } from 'lucide-react';
import { mockSuppliers } from '../data/mockData';
import { StatusBadge } from '../components/StatusBadge';
export function SupplierKPIDetail() {
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const navigate = useNavigate();
  const supplier = mockSuppliers.find(s => s.id === id);
  if (!supplier) {
    return <div className="flex flex-col items-center justify-center h-full" data-id="element-2855">
        <h2 className="text-xl font-bold text-gray-900" data-id="element-2856">Supplier not found</h2>
        <button onClick={() => navigate('/supplier-kpi')} className="mt-4 text-indigo-600 hover:text-indigo-800" data-id="element-2857">
          Back to KPI Report
        </button>
      </div>;
  }
  const deliveryScore = Math.max(0, 100 - supplier.deliveryDelayPercent * 5);
  const qualityScore = Math.max(0, 100 - supplier.rejectionRatePercent * 10);
  return <div className="max-w-7xl mx-auto space-y-6 pb-12" data-id="element-2858">
      {/* Header */}
      <div className="flex items-center justify-between" data-id="element-2859">
        <div className="flex items-center space-x-4" data-id="element-2860">
          <button onClick={() => navigate('/supplier-kpi')} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors" data-id="element-2861">
            <ArrowLeftIcon className="w-5 h-5" data-id="element-2862" />
          </button>
          <div data-id="element-2863">
            <div className="flex items-center space-x-3" data-id="element-2864">
              <h1 className="text-2xl font-bold text-gray-900 tracking-tight" data-id="element-2865">
                {supplier.name}
              </h1>
              <StatusBadge status={supplier.performanceStatus} data-id="element-2866" />
            </div>
            <p className="text-sm text-gray-500 mt-1" data-id="element-2867">
              {supplier.code} • {supplier.category} • {supplier.countryOfOrigin}
            </p>
          </div>
        </div>
        <div className="flex space-x-3" data-id="element-2868">
          <button className="px-4 py-2 border border-gray-300 bg-white text-gray-700 text-sm font-medium rounded-md hover:bg-gray-50 transition-colors shadow-sm flex items-center" data-id="element-2869">
            <FileTextIcon className="w-4 h-4 mr-2" data-id="element-2870" />
            Export Report
          </button>
        </div>
      </div>

      {/* KPI Overview */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-id="element-2871">
        <div className="p-6 border-b border-gray-200 bg-gray-50" data-id="element-2872">
          <h2 className="text-lg font-medium text-gray-900 flex items-center" data-id="element-2873">
            <TrendingUpIcon className="w-5 h-5 mr-2 text-indigo-600" data-id="element-2874" />
            KPI Score Breakdown
          </h2>
        </div>
        <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-8 divide-y md:divide-y-0 md:divide-x divide-gray-200" data-id="element-2875">
          <div className="flex flex-col items-center justify-center text-center" data-id="element-2876">
            <div className="text-sm font-medium text-gray-500 mb-2" data-id="element-2877">
              Delivery Score (60%)
            </div>
            <div className="relative inline-flex items-center justify-center" data-id="element-2878">
              <svg className="w-32 h-32 transform -rotate-90" data-id="element-2879">
                <circle cx="64" cy="64" r="56" stroke="currentColor" strokeWidth="12" fill="transparent" className="text-gray-100" data-id="element-2880" />
                <circle cx="64" cy="64" r="56" stroke="currentColor" strokeWidth="12" fill="transparent" strokeDasharray={351.858} strokeDashoffset={351.858 - 351.858 * deliveryScore / 100} className="text-blue-500" strokeLinecap="round" data-id="element-2881" />
              </svg>
              <span className="absolute text-3xl font-bold text-gray-900" data-id="element-2882">
                {deliveryScore}
              </span>
            </div>
            <div className="mt-4 text-sm text-gray-600" data-id="element-2883">
              Based on {supplier.deliveryDelayPercent}% delay rate
            </div>
          </div>

          <div className="flex flex-col items-center justify-center text-center pt-8 md:pt-0" data-id="element-2884">
            <div className="text-sm font-medium text-gray-500 mb-2" data-id="element-2885">
              Quality Score (40%)
            </div>
            <div className="relative inline-flex items-center justify-center" data-id="element-2886">
              <svg className="w-32 h-32 transform -rotate-90" data-id="element-2887">
                <circle cx="64" cy="64" r="56" stroke="currentColor" strokeWidth="12" fill="transparent" className="text-gray-100" data-id="element-2888" />
                <circle cx="64" cy="64" r="56" stroke="currentColor" strokeWidth="12" fill="transparent" strokeDasharray={351.858} strokeDashoffset={351.858 - 351.858 * qualityScore / 100} className="text-emerald-500" strokeLinecap="round" data-id="element-2889" />
              </svg>
              <span className="absolute text-3xl font-bold text-gray-900" data-id="element-2890">
                {qualityScore}
              </span>
            </div>
            <div className="mt-4 text-sm text-gray-600" data-id="element-2891">
              Based on {supplier.rejectionRatePercent}% rejection rate
            </div>
          </div>

          <div className="flex flex-col items-center justify-center text-center pt-8 md:pt-0" data-id="element-2892">
            <div className="text-sm font-medium text-gray-500 mb-2" data-id="element-2893">
              Final KPI Score
            </div>
            <div className="relative inline-flex items-center justify-center" data-id="element-2894">
              <svg className="w-40 h-40 transform -rotate-90" data-id="element-2895">
                <circle cx="80" cy="80" r="70" stroke="currentColor" strokeWidth="16" fill="transparent" className="text-gray-100" data-id="element-2896" />
                <circle cx="80" cy="80" r="70" stroke="currentColor" strokeWidth="16" fill="transparent" strokeDasharray={439.82} strokeDashoffset={439.82 - 439.82 * supplier.kpiScore / 100} className={supplier.kpiScore >= 90 ? 'text-emerald-500' : supplier.kpiScore >= 75 ? 'text-blue-500' : supplier.kpiScore >= 60 ? 'text-amber-500' : 'text-rose-500'} strokeLinecap="round" data-id="element-2897" />
              </svg>
              <div className="absolute flex flex-col items-center" data-id="element-2898">
                <span className="text-4xl font-bold text-gray-900" data-id="element-2899">
                  {supplier.kpiScore}
                </span>
                <span className="text-xs font-medium text-gray-500 mt-1 uppercase tracking-wider" data-id="element-2900">
                  {supplier.performanceStatus}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-2901">
        {/* Delivery Breakdown */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-id="element-2902">
          <div className="p-5 border-b border-gray-200 bg-gray-50 flex items-center justify-between" data-id="element-2903">
            <h2 className="text-lg font-medium text-gray-900 flex items-center" data-id="element-2904">
              <TruckIcon className="w-5 h-5 mr-2 text-blue-600" data-id="element-2905" />
              Delivery Performance
            </h2>
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800" data-id="element-2906">
              {supplier.totalDeliveries} Total Deliveries
            </span>
          </div>
          <div className="p-6" data-id="element-2907">
            <div className="grid grid-cols-2 gap-4 mb-6" data-id="element-2908">
              <div className="bg-emerald-50 rounded-lg p-4 border border-emerald-100" data-id="element-2909">
                <div className="flex items-center text-emerald-800 mb-1" data-id="element-2910">
                  <CheckCircleIcon className="w-4 h-4 mr-1.5" data-id="element-2911" />
                  <span className="text-sm font-medium" data-id="element-2912">On Time</span>
                </div>
                <div className="text-2xl font-bold text-emerald-900" data-id="element-2913">
                  {supplier.onTimeDeliveries}
                </div>
              </div>
              <div className="bg-rose-50 rounded-lg p-4 border border-rose-100" data-id="element-2914">
                <div className="flex items-center text-rose-800 mb-1" data-id="element-2915">
                  <AlertCircleIcon className="w-4 h-4 mr-1.5" data-id="element-2916" />
                  <span className="text-sm font-medium" data-id="element-2917">Delayed</span>
                </div>
                <div className="text-2xl font-bold text-rose-900" data-id="element-2918">
                  {supplier.delayedDeliveries}
                </div>
              </div>
              <div className="bg-amber-50 rounded-lg p-4 border border-amber-100" data-id="element-2919">
                <div className="flex items-center text-amber-800 mb-1" data-id="element-2920">
                  <ClockIcon className="w-4 h-4 mr-1.5" data-id="element-2921" />
                  <span className="text-sm font-medium" data-id="element-2922">Pending</span>
                </div>
                <div className="text-2xl font-bold text-amber-900" data-id="element-2923">
                  {supplier.pendingDeliveries}
                </div>
              </div>
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 flex flex-col justify-center" data-id="element-2924">
                <div className="text-sm font-medium text-gray-500 mb-1" data-id="element-2925">
                  Delay Rate
                </div>
                <div className="text-2xl font-bold text-gray-900" data-id="element-2926">
                  {supplier.deliveryDelayPercent}%
                </div>
              </div>
            </div>

            <div className="space-y-2" data-id="element-2927">
              <div className="flex justify-between text-sm" data-id="element-2928">
                <span className="text-gray-500" data-id="element-2929">
                  On Time (
                  {Math.round(supplier.onTimeDeliveries / supplier.totalDeliveries * 100)}
                  %)
                </span>
                <span className="font-medium text-gray-900" data-id="element-2930">
                  {supplier.onTimeDeliveries}
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2" data-id="element-2931">
                <div className="bg-emerald-500 h-2 rounded-full" style={{
                width: `${supplier.onTimeDeliveries / supplier.totalDeliveries * 100}%`
              }} data-id="element-2932"></div>
              </div>

              <div className="flex justify-between text-sm pt-2" data-id="element-2933">
                <span className="text-gray-500" data-id="element-2934">
                  Delayed (
                  {Math.round(supplier.delayedDeliveries / supplier.totalDeliveries * 100)}
                  %)
                </span>
                <span className="font-medium text-gray-900" data-id="element-2935">
                  {supplier.delayedDeliveries}
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2" data-id="element-2936">
                <div className="bg-rose-500 h-2 rounded-full" style={{
                width: `${supplier.delayedDeliveries / supplier.totalDeliveries * 100}%`
              }} data-id="element-2937"></div>
              </div>
            </div>
          </div>
        </div>

        {/* Quality Breakdown */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-id="element-2938">
          <div className="p-5 border-b border-gray-200 bg-gray-50 flex items-center justify-between" data-id="element-2939">
            <h2 className="text-lg font-medium text-gray-900 flex items-center" data-id="element-2940">
              <ShieldCheckIcon className="w-5 h-5 mr-2 text-emerald-600" data-id="element-2941" />
              Quality Performance
            </h2>
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800" data-id="element-2942">
              {supplier.totalItemsInspected} Items Inspected
            </span>
          </div>
          <div className="p-6" data-id="element-2943">
            <div className="grid grid-cols-2 gap-4 mb-6" data-id="element-2944">
              <div className="bg-emerald-50 rounded-lg p-4 border border-emerald-100" data-id="element-2945">
                <div className="flex items-center text-emerald-800 mb-1" data-id="element-2946">
                  <CheckCircleIcon className="w-4 h-4 mr-1.5" data-id="element-2947" />
                  <span className="text-sm font-medium" data-id="element-2948">Accepted</span>
                </div>
                <div className="text-2xl font-bold text-emerald-900" data-id="element-2949">
                  {supplier.itemsAccepted}
                </div>
              </div>
              <div className="bg-rose-50 rounded-lg p-4 border border-rose-100" data-id="element-2950">
                <div className="flex items-center text-rose-800 mb-1" data-id="element-2951">
                  <XCircleIcon className="w-4 h-4 mr-1.5" data-id="element-2952" />
                  <span className="text-sm font-medium" data-id="element-2953">Rejected</span>
                </div>
                <div className="text-2xl font-bold text-rose-900" data-id="element-2954">
                  {supplier.itemsRejected}
                </div>
              </div>
              <div className="bg-amber-50 rounded-lg p-4 border border-amber-100" data-id="element-2955">
                <div className="flex items-center text-amber-800 mb-1" data-id="element-2956">
                  <AlertTriangleIcon className="w-4 h-4 mr-1.5" data-id="element-2957" />
                  <span className="text-sm font-medium" data-id="element-2958">Under Review</span>
                </div>
                <div className="text-2xl font-bold text-amber-900" data-id="element-2959">
                  {supplier.itemsUnderReview}
                </div>
              </div>
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 flex flex-col justify-center" data-id="element-2960">
                <div className="text-sm font-medium text-gray-500 mb-1" data-id="element-2961">
                  Rejection Rate
                </div>
                <div className="text-2xl font-bold text-gray-900" data-id="element-2962">
                  {supplier.rejectionRatePercent}%
                </div>
              </div>
            </div>

            <div className="space-y-2" data-id="element-2963">
              <div className="flex justify-between text-sm" data-id="element-2964">
                <span className="text-gray-500" data-id="element-2965">
                  Accepted (
                  {Math.round(supplier.itemsAccepted / supplier.totalItemsInspected * 100)}
                  %)
                </span>
                <span className="font-medium text-gray-900" data-id="element-2966">
                  {supplier.itemsAccepted}
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2" data-id="element-2967">
                <div className="bg-emerald-500 h-2 rounded-full" style={{
                width: `${supplier.itemsAccepted / supplier.totalItemsInspected * 100}%`
              }} data-id="element-2968"></div>
              </div>

              <div className="flex justify-between text-sm pt-2" data-id="element-2969">
                <span className="text-gray-500" data-id="element-2970">
                  Rejected (
                  {Math.round(supplier.itemsRejected / supplier.totalItemsInspected * 100)}
                  %)
                </span>
                <span className="font-medium text-gray-900" data-id="element-2971">
                  {supplier.itemsRejected}
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2" data-id="element-2972">
                <div className="bg-rose-500 h-2 rounded-full" style={{
                width: `${supplier.itemsRejected / supplier.totalItemsInspected * 100}%`
              }} data-id="element-2973"></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Recommendations */}
      <div className={`rounded-xl border p-6 ${supplier.kpiScore >= 75 ? 'bg-emerald-50 border-emerald-200' : supplier.kpiScore >= 60 ? 'bg-amber-50 border-amber-200' : 'bg-rose-50 border-rose-200'}`} data-id="element-2974">
        <h3 className={`text-lg font-medium mb-2 ${supplier.kpiScore >= 75 ? 'text-emerald-900' : supplier.kpiScore >= 60 ? 'text-amber-900' : 'text-rose-900'}`} data-id="element-2975">
          System Recommendation
        </h3>
        <p className={`text-sm ${supplier.kpiScore >= 75 ? 'text-emerald-800' : supplier.kpiScore >= 60 ? 'text-amber-800' : 'text-rose-800'}`} data-id="element-2976">
          {supplier.kpiScore >= 90 ? 'This supplier is performing exceptionally well. Consider them for strategic partnerships and long-term contracts. They are eligible for preferred supplier status.' : supplier.kpiScore >= 75 ? 'This supplier is performing well and meeting expectations. Continue regular monitoring. They are eligible for standard contract renewals.' : supplier.kpiScore >= 60 ? 'This supplier requires performance improvement. Schedule a review meeting to discuss delivery and quality issues. Consider short-term contracts until performance improves.' : 'This supplier is severely underperforming. Immediate corrective action plan required. Consider suspending new orders until issues are resolved or initiating vendor replacement.'}
        </p>
      </div>
    </div>;
}